import { useAccounts } from "../../api/hooks/useAccounts";
import { useCreateAccount } from "../../api/hooks/useCreateAccount";
import { useDeleteAccount } from "../../api/hooks/useDeleteAccount";
import { useRestoreAccount } from "../../api/hooks/useRestoreAccount";
import { useUpdateAccount } from "../../api/hooks/useUpdateAccount";
import type { components } from "../../api/schema";
import type {
  NamedResourceAdapter,
  NormalizedFireAndForget,
  NormalizedMutation,
} from "../named_resource_crud/types";

export type AccountResponse = components["schemas"]["AccountResponse"];

function useUpdateAccountAdapted(): NormalizedMutation<{
  id: string;
  body: { name: string };
}> {
  const m = useUpdateAccount();
  return {
    mutateAsync: ({ id, body }) => m.mutateAsync({ accountId: id, body }),
    isPending: m.isPending,
  };
}

function useDeleteAccountAdapted(): NormalizedMutation<{ id: string }> {
  const m = useDeleteAccount();
  return {
    mutateAsync: ({ id }) => m.mutateAsync({ accountId: id }),
    isPending: m.isPending,
  };
}

function useRestoreAccountAdapted(): NormalizedFireAndForget<{ id: string }> {
  const m = useRestoreAccount();
  return {
    mutate: ({ id }) => {
      m.mutate({ accountId: id });
    },
    isPending: m.isPending,
  };
}

export const accountsAdapter: NamedResourceAdapter<AccountResponse> = {
  singularLabel: "account",
  pluralLabel: "accounts",
  pluralLabelCapitalized: "Accounts",
  pageDescription: "Manage the trading accounts trades are recorded against.",
  emptyMessage: 'No accounts yet. Click "New account" to add one.',
  loadErrorMessage: "Failed to load accounts.",
  createDescription:
    "Add a new trading account. You can rename or delete it later.",
  editDescription:
    "Rename this account. Trades and history remain attached.",
  placeholder: "Main brokerage",
  tableTestId: "accounts-table",

  useList: useAccounts,
  useCreate: useCreateAccount,
  useUpdate: useUpdateAccountAdapted,
  useDelete: useDeleteAccountAdapted,
  useRestore: useRestoreAccountAdapted,

  getId: (item) => item.account_id,
  isDeleted: (item) => item.is_deleted,
  getName: (item) => item.name,
  getCreatedAt: (item) => item.created_at,
};
